// src/features/capacity/capacityModel.js
//
// PLACES-CAPACITY-UNIFIED — pure derivations for the Capacity lens.
// Inputs come from GET /api/plan/capacity (see getCapacity in marketsClient).
// DECISION (Corey, 2026-10-31) governs where it conflicts with the brief/mock:
//   * Demand = Pressure's projected assigned headcount per month-start.
//   * Baseline supply = schedule-effective installed seats (as-committed), no
//     attendance inflation. Maximum = recorded maxCapacity + effective headroom.
//   * Scenario growth compounds on top of demand; every other control acts on
//     SUPPLY (usable and attendance-adjusted maximum alike), neutral by default.

import { peakRatio, computeUsableSeats } from "../plan/marketsService.js";

// ─── Constants ───────────────────────────────────────────────────────────────
export const MONTHLY_PERIODS = 36;
export const LONG_RANGE_START = 24;
export const QUARTERLY_PERIODS = 12;
export const DEFAULT_TARGET_BUFFER_PCT = 10;
// The prior Scenario normalised attendance against 10 days/month (WHATIF_DEFAULT_LEVERS).
export const DAYS_BASELINE = 10;
export const LONG_RANGE_NOTE = "Beyond 24 months: long-range projection, lower confidence.";
export const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const STATUS = Object.freeze({
  COMFORTABLE: "comfortable", TIGHT: "tight", DENSIFY: "densify", OVER: "over",
  NO_DATA: "no_data", HISTORY: "history",
});
export const STATUS_LABEL = {
  comfortable: "Comfortable", tight: "Tight", densify: "Densify", over: "Over",
  no_data: "No data", history: "Under 3 months history",
};
// Worst-first ordering used for quarterly representatives and scenario comparisons.
// Data-quality states rank below every real status so a single gap month never
// hides a measurable constituent month.
export const STATUS_RANK = { history: 0, no_data: 1, comfortable: 2, tight: 3, densify: 4, over: 5 };

// Lever definitions. `neutral` is the no-change value: at neutral every
// scenario figure equals baseline exactly (supply factor 1, growth 0).
export const LEVERS = Object.freeze([
  { key: "growth", label: "Net growth", unit: "% / yr", neutral: 0, min: -20, max: 40, step: 1 },
  { key: "days", label: "Days in office / mo", unit: "days", neutral: DAYS_BASELINE, min: 4, max: 20, step: 1 },
  { key: "sharing", label: "Desk sharing ratio", unit: ":1", neutral: 1, min: 0.8, max: 2, step: 0.1 },
  { key: "hold", label: "Hoteling / visitor hold", unit: "%", neutral: 0, min: 0, max: 20, step: 1 },
  { key: "conf", label: "Conf-room give-back", unit: "%", neutral: 0, min: 0, max: 20, step: 1 },
  { key: "waste", label: "Team-block waste", unit: "%", neutral: 0, min: 0, max: 20, step: 1 },
]);
export const NEUTRAL_LEVERS = Object.freeze(Object.fromEntries(LEVERS.map((l) => [l.key, l.neutral])));

// ─── Small helpers ───────────────────────────────────────────────────────────
const fin = (n) => typeof n === "number" && Number.isFinite(n);
export function monthKey(start) { return String(start ?? "").slice(0, 7); }
export function monthLabel(start) {
  const [y, m] = monthKey(start).split("-").map(Number);
  return y && m ? `${MONTH_ABBR[m - 1]} ${y}` : "";
}
export function formatLever(key, value) {
  if (key === "growth") return `${value > 0 ? "+" : ""}${value}%`;
  if (key === "days") return `${value} days`;
  if (key === "sharing") return `${Number(value).toFixed(1)}:1`;
  return `${value}%`;
}

export function isScenarioActive(levers) {
  return LEVERS.some((l) => Math.abs((levers?.[l.key] ?? l.neutral) - l.neutral) > 1e-9);
}
export function changedLevers(levers) {
  return LEVERS.filter((l) => Math.abs((levers?.[l.key] ?? l.neutral) - l.neutral) > 1e-9)
    .map((l) => ({ key: l.key, label: l.label, value: levers[l.key], text: `${l.label} ${formatLever(l.key, levers[l.key])}` }));
}

// ─── Scenario math ───────────────────────────────────────────────────────────
// Effective-seat multiplier for the supply-side controls.
//
// Days in office: the retired Scenario tab multiplied DEMAND by
//   rtoFactor = peakRatio(days) / peakRatio(10)
// Per the DECISION it now scales SUPPLY by the reciprocal
//   peakRatio(10) / peakRatio(days)
// so demand / (seats / rtoFactor) === (demand * rtoFactor) / seats: the
// people-per-seat ratio, and therefore every status, is identical for the same
// inputs. These are changes relative to today's observed attendance-adjusted
// Usable baseline, NOT a second division of installed seats by attendance.
// The same factor multiplies usable and adjusted maximum seats.
//
// Sharing / hold / give-back / waste reuse computeUsableSeats on one seat; at
// neutral (1:1, 0%, 0%, 0%) that is exactly 1.
export function supplyFactor(levers = NEUTRAL_LEVERS) {
  const l = { ...NEUTRAL_LEVERS, ...levers };
  const usable = computeUsableSeats(1, {
    sharingRatio: l.sharing, hotelingHoldPct: l.hold, confGivebackPct: l.conf, teamBlockWastePct: l.waste,
  });
  return usable * (peakRatio(DAYS_BASELINE) / peakRatio(l.days));
}

// Growth compounds annually on top of Pressure's projection, pro-rated by
// month index (0 = current month).
export function growthFactor(levers, monthIndex) {
  const g = (levers?.growth ?? 0) / 100;
  return Math.pow(1 + g, Math.max(0, monthIndex) / 12);
}

// One market-month evaluated for baseline (levers omitted / neutral) or scenario.
export function evaluateMonth(month, monthIndex, levers = null, bufferPct = DEFAULT_TARGET_BUFFER_PCT) {
  const active = levers && isScenarioActive(levers);
  const sf = active ? supplyFactor(levers) : 1;
  const gf = active ? growthFactor(levers, monthIndex) : 1;
  const demand = fin(month?.projectedHc) ? month.projectedHc * gf : null;
  const seats = !month?.missingCapacity && fin(month?.usableSeats) ? month.usableSeats * sf : null;
  const max = fin(month?.maximumSeats) ? month.maximumSeats * sf : null;
  let status;
  if (demand == null) status = STATUS.NO_DATA;
  else if (month?.insufficientHistory) status = STATUS.HISTORY;
  else if (month?.missingCapacity || month?.missingAttendance) status = STATUS.NO_DATA;
  else status = classify(demand, seats, max, bufferPct);
  const ratio = demand != null && seats != null ? (seats > 0 ? demand / seats : (demand > 0 ? Infinity : 0)) : null;
  return { monthIndex, start: month?.start, demand, seats, max,
    installedSeats: month?.missingCapacity ? null : month?.installedSeats ?? null, recordedMax: month?.recordedMaximumSeats ?? null,
    // Disclosure-only inputs from the read model (owner decision, #9048):
    // pre-history usable estimates and recorded maxima ignored below installed.
    attendanceEstimates: Array.isArray(month?.attendanceEstimates) ? month.attendanceEstimates : [],
    invalidMaximums: Array.isArray(month?.invalidMaximums) ? month.invalidMaximums : [],
    unopenedOffices: Array.isArray(month?.unopenedOffices) ? month.unopenedOffices : [],
    missingInputs: [
      ...(!fin(month?.projectedHc) ? ["projected demand"] : []),
      ...(month?.missingCapacity ? ["capacity schedule"] : []),
      ...(month?.missingAttendance ? ["attendance"] : []),
    ],
    status, ratio, shade: comfortShade(status, ratio) };
}

// Shared ladder (grid, rows, tooltip, key, KPIs, comparisons).
export function classify(demand, seats, max, bufferPct = DEFAULT_TARGET_BUFFER_PCT) {
  if (!fin(demand) || !fin(seats)) return STATUS.NO_DATA;
  const hasMax = fin(max);
  if (hasMax && max < seats && demand > max) return STATUS.OVER;
  if (seats <= 0) {
    if (demand <= 0) return STATUS.COMFORTABLE;      // zero demand on zero seats is 0%
    return hasMax && max > 0 && demand <= max ? STATUS.DENSIFY : STATUS.OVER;
  }
  const ratio = demand / seats;
  const threshold = (100 - (fin(bufferPct) ? bufferPct : DEFAULT_TARGET_BUFFER_PCT)) / 100;
  if (ratio < threshold) return STATUS.COMFORTABLE;
  // Owner decision (#9048): Tight includes equality (<= 100% of usable).
  if (demand <= seats) return STATUS.TIGHT;
  // Densify only when demand exceeds usable and stays within recorded headroom.
  if (hasMax && max > seats && demand <= max) return STATUS.DENSIFY;
  return STATUS.OVER;
}

// Comfortable fullness shade: 1 (<60%), 2 (<80%), 3 (to the buffer threshold).
export function comfortShade(status, ratio) {
  if (status !== STATUS.COMFORTABLE) return null;
  if (!fin(ratio) || ratio < 0.6) return 1;
  return ratio < 0.8 ? 2 : 3;
}

// ─── Periods ─────────────────────────────────────────────────────────────────
// months: API months [{start,index,longRange}] beginning with the current month.
export function buildPeriods(months, view) {
  const list = (months ?? []).slice(0, MONTHLY_PERIODS);
  if (view !== "quarterly") {
    return list.slice(0, MONTHLY_PERIODS).map((m, i) => {
      const [y, mo] = monthKey(m.start).split("-").map(Number);
      return {
        key: monthKey(m.start), monthIdxs: [i], label: mo === 1 ? `'${String(y).slice(2)}` : MONTH_ABBR[mo - 1][0],
        full: monthLabel(m.start), compact: monthLabel(m.start), yearStart: mo === 1, longRange: !!m.longRange || i >= LONG_RANGE_START,
      };
    });
  }
  const out = [];
  list.forEach((m, i) => {
    const [y, mo] = monthKey(m.start).split("-").map(Number);
    const q = Math.floor((mo - 1) / 3) + 1;
    const key = `${y}-Q${q}`;
    const last = out[out.length - 1];
    if (last && last.key === key) last.monthIdxs.push(i);
    else {
      out.push({ key, monthIdxs: [i], label: q === 1 || out.length === 0 ? `Q${q} '${String(y).slice(2)}` : `Q${q}`, full: `Q${q} ${y}`, yearStart: q === 1 || out.length === 0 });
    }
  });
  return out.map((p) => ({
    ...p, compact: p.full, partial: p.monthIdxs.length < 3,
    full: p.monthIdxs.length < 3
      ? `${p.full} (${monthLabel(list[p.monthIdxs[0]].start)}–${monthLabel(list[p.monthIdxs.at(-1)].start)} only)`
      : p.full,
  }));
}

// Representative month: worst status, then highest ratio. Past months never
// appear because the API's first month is the current month.
export function pickRepresentative(evals) {
  let best = null;
  for (const e of evals) {
    if (!e) continue;
    const r = STATUS_RANK[e.status];
    const rr = fin(e.ratio) ? e.ratio : (e.ratio === Infinity ? Number.MAX_VALUE : -1);
    if (!best || r > best.r || (r === best.r && rr > best.rr)) best = { e, r, rr };
  }
  return best?.e ?? null;
}

export function evaluatePeriod(market, period, months, levers, bufferPct) {
  const evals = period.monthIdxs.map((i) => evaluateMonth(market.months?.[i], i, levers, bufferPct));
  const rep = pickRepresentative(evals);
  const longRange = rep ? (!!months?.[rep.monthIndex]?.longRange || rep.monthIndex >= LONG_RANGE_START) : false;
  return { ...rep, longRange };
}

export function periodIndexForFocus(periods, months, focusKey) {
  if (!focusKey) return 0;
  const idx = (months ?? []).findIndex((m) => monthKey(m.start) === focusKey);
  if (idx < 0) return 0;
  const p = periods.findIndex((pp) => pp.monthIdxs.includes(idx));
  if (p >= 0) return p;
  // Focus beyond this view's window (e.g. a third-year quarter shown in
  // Monthly): clamp to the last visible period; the URL keeps the date so
  // switching back restores the original quarter.
  const lastIdx = periods[periods.length - 1]?.monthIdxs.at(-1) ?? -1;
  return idx > lastIdx ? Math.max(0, periods.length - 1) : 0;
}

// ─── Commitments ─────────────────────────────────────────────────────────────
// Only seat-changing schedule rows; unknown identities never read as projects.
export function commitmentLabel(c) {
  const name = c.projectId && c.projectName ? c.projectName : (c.label || "Capacity schedule change");
  const d = Math.round(c.delta);
  return `${name} · ${d > 0 ? "+" : "\u2212"}${Math.abs(d)} seats`;
}
export function commitmentsForPeriod(market, period, months) {
  const keys = new Set(period.monthIdxs.map((i) => monthKey(months?.[i]?.start)));
  return (market.commitments ?? []).filter((c) => fin(c.delta) && c.delta !== 0 && keys.has(monthKey(c.effectiveDate)));
}

// ─── Seat grid ───────────────────────────────────────────────────────────────
export function seatGroups(demand, seats, max, installed = seats) {
  if (!fin(demand) || !fin(seats)) return null;
  const d = Math.max(0, Math.round(demand));
  const s = Math.max(0, Math.round(seats));
  const m = fin(max) ? Math.max(0, Math.round(max)) : s;
  const room = Math.max(0, m - s);
  const used = Math.min(d, s, m);
  const spare = Math.max(0, s - d);
  const densUsed = Math.min(Math.max(0, d - s), room);
  const densRoom = room - densUsed;
  const over = Math.max(0, d - m);
  const per = installed > 150 ? 5 : 1;
  const sq = (n) => Math.ceil(n / per);
  const counts = { used, spare, densUsed, densRoom, over };
  const squares = Object.fromEntries(Object.entries(counts).map(([k, v]) => [k, sq(v)]));
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const partial = per > 1 ? Object.entries(counts).filter(([, v]) => v % per !== 0).map(([k, v]) => ({ group: k, seats: v % per })) : [];
  let summary;
  if (over > 0) summary = { kind: "over", value: over, text: fin(max) ? `${over} over max` : `${over} above usable` };
  else if (d > s) summary = { kind: "above", value: d - s, text: `${d - s} above usable` };
  else summary = { kind: "spare", value: s - d, text: `${s - d} spare` };
  return { demand: d, seats: s, max: fin(max) ? m : null, per, counts, squares, total, partial, summary };
}

// ─── Aggregates ──────────────────────────────────────────────────────────────
export function evaluateGrid(markets, periods, months, levers, bufferPct) {
  return (markets ?? []).map((market) => ({
    market,
    // API month zero is today's month, independent of view/focus. Keep this
    // series separate from quarterly representatives and their issue horizon.
    current: {
      baseline: evaluateMonth(market.months?.[0], 0, null, bufferPct),
      scenario: evaluateMonth(market.months?.[0], 0, levers, bufferPct),
    },
    baseline: periods.map((p) => evaluatePeriod(market, p, months, null, bufferPct)),
    scenario: levers && isScenarioActive(levers) ? periods.map((p) => evaluatePeriod(market, p, months, levers, bufferPct)) : null,
  }));
}

// Status KPIs use rows in view at focus; spare uses the same rows this month.
// The horizon over-count + first-over
// period is kept separately for Next wall and the Strained/Holds result.
export function summarize(rows, periods, focusIdx, which = "baseline") {
  let over = 0, tightOrDensify = 0, spare = 0, horizonOver = 0, wall = null;
  for (const row of rows) {
    const cells = row[which] ?? row.baseline;
    let o = false;
    cells.forEach((c, i) => {
      if (c?.status === STATUS.OVER) { o = true; if (wall == null || i < wall) wall = i; }
    });
    if (o) horizonOver += 1;
    const f = cells[focusIdx];
    if (f?.status === STATUS.OVER) over += 1;
    if (f?.status === STATUS.TIGHT || f?.status === STATUS.DENSIFY) tightOrDensify += 1;
    const current = row.current?.[which];
    if (current && current.status !== STATUS.NO_DATA && current.status !== STATUS.HISTORY && fin(current.demand) && fin(current.seats)) {
      spare += Math.max(0, current.seats - current.demand);
    }
  }
  return { over, tightOrDensify, spare: Math.round(spare), horizonOver, wallIdx: wall,
    wallCompact: wall == null ? null : periods[wall]?.compact ?? periods[wall]?.full ?? null,
    wallLabel: wall == null ? null : periods[wall]?.full ?? null };
}

export function scenarioResult(summary) {
  return summary.horizonOver > 0
    ? { tone: "strained", text: `Strained · ${summary.horizonOver} market${summary.horizonOver === 1 ? "" : "s"} over, first in ${summary.wallLabel}` }
    : { tone: "holds", text: "Holds · no market over in view" };
}
// Lower is better for every capacity KPI except spare seats.
export function deltaTone(today, scenario, higherIsBetter = false) {
  if (today === scenario || today == null || scenario == null) return "flat";
  const better = higherIsBetter ? scenario > today : scenario < today;
  return better ? "good" : "bad";
}

// ─── Row summaries (NOW / FIRST ISSUE) ───────────────────────────────────────
// One derivation shared by the grid columns and the default issue-first sort,
// so both always agree on horizon (displayed periods) and series.
export const ISSUE_STATUSES = ["tight", "densify", "over"];
export const ISSUE_SEVERITY = { over: 3, densify: 2, tight: 1 };
export const ATTENDANCE_ESTIMATE_PHRASE = "usable estimated from market attendance until 3 months of history";

export function rowSummary(row, scenarioOn = false) {
  const cells = (scenarioOn && row.scenario ? row.scenario : row.baseline) ?? [];
  const n = row.current?.[scenarioOn && row.scenario ? "scenario" : "baseline"] ?? null;
  // Missing inputs stay unknown (null), never zero-filled.
  const now = n ? { demand: fin(n.demand) ? n.demand : null, seats: fin(n.seats) ? n.seats : null, status: n.status ?? null } : null;
  let firstIssue = null;
  for (let i = 0; i < cells.length; i += 1) {
    if (ISSUE_STATUSES.includes(cells[i]?.status)) { firstIssue = { idx: i, status: cells[i].status }; break; }
  }
  return { now, firstIssue };
}

// Comparator for the default sort: earliest issue, then Over > Densify > Tight,
// then market name; rows without an issue follow dated issues.
export function compareByIssue(a, b, sa, sb) {
  const ia = sa.firstIssue?.idx ?? Infinity, ib = sb.firstIssue?.idx ?? Infinity;
  if (ia !== ib) return ia < ib ? -1 : 1;
  const va = ISSUE_SEVERITY[sa.firstIssue?.status] ?? 0, vb = ISSUE_SEVERITY[sb.firstIssue?.status] ?? 0;
  if (va !== vb) return vb - va;
  return (a.market.name ?? "").localeCompare(b.market.name ?? "");
}

// Disclosure lines for estimates and ignored maxima (tooltip + expanded row).
export function disclosureLines(e) {
  const out = [];
  for (const office of new Map((e?.unopenedOffices ?? []).map((o) => [o.locationId, o])).values()) {
    out.push(`${office.locationAlias || office.locationId} has no capacity schedule or online date yet.`);
  }
  if (e?.missingInputs?.length) out.push(`No capacity forecast: ${e.missingInputs.join(", ")}.`);
  for (const a of e?.attendanceEstimates ?? []) {
    const who = a.locationAlias || a.locationId || "Office";
    const f = fin(a.factor) ? Math.round(a.factor * 100) / 100 : "\u2013";
    out.push(`${who}: ${ATTENDANCE_ESTIMATE_PHRASE} (factor ${f})`);
    if (a.source === "default") out.push(`${who}: no market attendance recorded; default attendance factor ${fin(a.factor) ? a.factor : 1} applied`);
  }
  for (const m of e?.invalidMaximums ?? []) {
    const who = m.locationAlias || m.locationId || "Office";
    out.push(`${who}: recorded maximum ${m.recordedMaximumSeats} below ${m.installedSeats} installed, ignored`);
  }
  return out;
}
